import React, { useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Table } from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  fetchSessionsList,
  fetchSuppliersList,
  fetchBillChargedTripsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchChargesBillById } from "../../../slices/InvoiceCharges/thunk";
import { createSelector } from "reselect";

const validationSchema = Yup.object({});

const EditExtraChargeBill = ({ billId, onSubmit, onCloseForm }) => {
  const [tripInitialized, setTripInitialized] = useState(false);
  const [allSelectedTrips, setAllSelectedTrips] = useState([]);

  const dispatch = useDispatch();

  const [totals, setTotals] = useState({
    tollCharges: 0,
    handlingCharges: 0,
    otherCharges: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      billChargedTrips: state.Trip.billChargedTrips,
      loading: state.Trip.loading,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,
      session: state.Session.sessions,
      billDetails: state.InvoiceCharges.selectedChargesBill,
      suppliers: state.Supplier.suppliers,
    }),
  );

  const {
    billChargedTrips,
    loading,
    customersList,
    session,
    billDetails,
    vehicles,
    suppliers,
  } = useSelector(selector);

  /* ---------------------------------------
     FETCH INITIAL DATA
  -----------------------------------------*/
  useEffect(() => {
    if (!billId) return;
    dispatch(fetchVehiclesList({ isPagination: 0, isActive: true }));
    dispatch(fetchCustomersList({ isPagination: 0 }));
    dispatch(fetchSessionsList({ isPagination: 0 }));
    dispatch(fetchSuppliersList({ isPagination: 0 }));
    dispatch(fetchChargesBillById(billId));
  }, [dispatch, billId]);

  /* ---------------------------------------
     WHEN BILL DETAILS ARRIVE → LOAD TRIPS
  -----------------------------------------*/
  useEffect(() => {
    setTripInitialized(false);
    if (billDetails?.customerId) {
      const payload = {
        customerId: billDetails?.customerId,
        sessionId: billDetails?.sessionId,
        vehicleId: billDetails?.chargesInvoiceResponses?.[0]?.vehicleId,
        chargesBillInvoiceNo: billDetails?.chargesInvoiceNo,
        isChargesBill: false,
        isUpdateChargesBill: true,
        tripType: mapPartyTypeToTripType(billDetails?.partyType),
      };

      // if (billDetails?.partyType) {
      //   payload.partyType = billDetails.partyType;
      // }

      dispatch(fetchBillChargedTripsList(payload));
    }
  }, [dispatch, billDetails?.customerId]);

  /* ---------------------------------------
     DATE PARSER
  -----------------------------------------*/
  const convertDate = (d) => {
    if (!d) return new Date();
    if (d instanceof Date && !isNaN(d)) return d;
    const parts = String(d).split("-");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const dt = new Date(`${year}-${month}-${day}`);
      return isNaN(dt) ? new Date() : dt;
    }
    const dt2 = new Date(d);
    return isNaN(dt2) ? new Date() : dt2;
  };

  /* ---------------------------------------
     TOTAL CALCULATION
  -----------------------------------------*/
  const calculateTotals = (selectedTripIds = []) => {
    const normalized = (selectedTripIds || []).map((id) => String(id));

    const selectedTrips = allSelectedTrips.filter((trip) =>
      normalized.includes(String(trip.id)),
    );

    const tollCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.tollCharges) || 0),
      0,
    );
    const handlingCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.handlingCharges) || 0),
      0,
    );
    const otherCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.otherCharges) || 0),
      0,
    );

    const grandTotal = tollCharges + handlingCharges + otherCharges;

    setTotals({
      tollCharges,
      handlingCharges,
      otherCharges,
      grandTotal,
    });
  };

  const initialTripIdsFromBill = (bd) => {
    if (!bd) return [];
    const arr = bd.chargesInvoiceResponses || [];
    return arr.map((x) => String(x.dailyTripId));
  };

  const initialSessionId = (bd) => bd?.sessionId || "";

  const originalVehicleId =
    billDetails?.chargesInvoiceResponses?.[0]?.vehicleId;

  const chargesInvoiceNo =
    billDetails?.chargesInvoiceNo || billDetails?.invoiceNo;

  const tripOptions = [
    { value: "KM", label: "OutStation" },
    { value: "perTrip", label: "Local" },
  ];
  const mapPartyTypeToTripType = (partyType) => {
    if (partyType === "OUTSTATION") return "KM";
    if (partyType === "LOCAL") return "perTrip";
    return "";
  };

  const originalTripsFromBill = (bd) =>
    bd?.chargesInvoiceResponses?.map((item) => ({
      id: item.dailyTripId,
      tripNo: item.tripNo,
      vehicleNo: item.vehicleNo,
      customerName: item.customerName,
      suppliersName: item.suppliersName,
      tripDate: item.tripDate,
      amount: item.amount,
      vehicleId: item.vehicleId,
      customerId: item.customerId,
      partyType: item.partyType,
      kmOrTrip: item.kmOrTrip,
      rate: item.rate,
      tollCharges: item.tollCharges,
      handlingCharges: item.handlingCharges,
      otherCharges: item.otherCharges,
    })) || [];

  const uniqueTripsById = (trips = []) =>
    trips
      .filter((trip) => trip?.id)
      .filter(
        (trip, index, arr) =>
          arr.findIndex((x) => String(x.id) === String(trip.id)) === index,
      );

  return (
    <div className="p-3">
      <h4 className="mb-3">Edit Bill </h4>

      <Formik
        enableReinitialize
        initialValues={{
          tollCharge: billDetails?.tollCharges || 0,
          otherCharge: billDetails?.otherCharges || 0,
          tripType: mapPartyTypeToTripType(billDetails?.partyType),
          handlingCharge: billDetails?.handlingCharges || 0,
          totalCharge:
            (billDetails?.tollCharges || 0) +
            (billDetails?.otherCharges || 0) +
            (billDetails?.handlingCharges || 0),
          customerId: billDetails?.customerId || "",
          customerAddress: billDetails?.customerAddress || "",
          customerGSTNo: billDetails?.customerGSTNo || "",
          billDate: convertDate(billDetails?.billDate),
          tripIds: initialTripIdsFromBill(billDetails),
          sessionId: initialSessionId(billDetails),
          vehicleId: billDetails?.chargesInvoiceResponses?.[0]?.vehicleId || "",
          note: billDetails?.note || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const existingMap = {};
          (billDetails?.chargesInvoiceResponses || []).forEach((item) => {
            existingMap[String(item.dailyTripId)] = item.id; // keep existing DB row id
          });

          const d = values.billDate;
          const formattedBillDate =
            ("0" + d.getDate()).slice(-2) +
            "-" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "-" +
            d.getFullYear();

          const id = billDetails?.id;

          const payload = {
            sessionId: values.sessionId,
            billDate: formattedBillDate,
            customerId: values.customerId,
            customerAddress: values.customerAddress || "",
            customerGSTNo: values.customerGSTNo || "",
            tollCharges: totals.tollCharges || 0,
            handlingCharges: totals.handlingCharges || 0,
            otherCharges: totals.otherCharges || 0,
            totalAmount: totals.grandTotal || 0,
            note: values.note || 0,
            partyType:
              values.tripType === "KM"
                ? "OUTSTATION"
                : values.tripType === "perTrip"
                  ? "LOCAL"
                  : null,

            chargesInvoiceRequests: (values.tripIds || []).map((tripId) => ({
              id: existingMap[String(tripId)] || null, // keep old row id, null for new
              dailyTripId: tripId,
            })),
          };

          if (onSubmit) onSubmit(id, payload);
          if (onCloseForm) onCloseForm();
        }}
      >
        {({ values, setFieldValue, setFieldTouched }) => {
          // useEffect(() => {
          //   if (trips.length > 0 && billDetails) {
          //     const ids = initialTripIdsFromBill(billDetails).map(String);
          //     setFieldValue("tripIds", ids);
          //     calculateTotals(ids);
          //   }
          // }, [trips, billDetails]);

          useEffect(() => {
            if (!tripInitialized && billDetails) {
              const originalBillTrips = originalTripsFromBill(billDetails);
              const billTrueTrips = billChargedTrips.filter(
                (t) => t.chargesBill === true,
              );

              const initialSelectedTrips = uniqueTripsById([
                ...originalBillTrips,
                ...billTrueTrips,
              ]);

              if (initialSelectedTrips.length === 0) return;

              const initialTripIds = initialSelectedTrips.map((trip) =>
                String(trip.id),
              );

              setFieldValue("tripIds", initialTripIds);
              setAllSelectedTrips(initialSelectedTrips);
              calculateTotals(initialTripIds);
              setTripInitialized(true);
            }
          }, [billChargedTrips, billDetails, tripInitialized]);

          useEffect(() => {
            calculateTotals(values.tripIds);
          }, [allSelectedTrips, values.tripIds]);

          const originalBillTrips = originalTripsFromBill(billDetails);
          const allTripOptions = uniqueTripsById([
            ...billChargedTrips,
            ...originalBillTrips,
          ]);

          const availableTrips = allTripOptions.filter(
            (trip) =>
              !allSelectedTrips.some((x) => String(x.id) === String(trip.id)),
          );

          const hasMixedSelection = (() => {
            if (allSelectedTrips.length <= 1) return false;

            const first = allSelectedTrips[0];

            return allSelectedTrips.some(
              (trip) =>
                String(trip.customerId) !== String(first.customerId) ||
                String(trip.vehicleId) !== String(first.vehicleId) ||
                String(trip.partyType) !== String(first.partyType),
            );
          })();

          const handleTripToggle = (tripId, checked) => {
            const id = String(tripId);
            const current = Array.isArray(values.tripIds)
              ? [...values.tripIds]
              : [];

            const updated = checked
              ? [...new Set([...current, id])]
              : current.filter((t) => t !== id);

            setFieldValue("tripIds", updated);

            const currentTrip = allTripOptions.find((t) => String(t.id) === id);

            if (checked && currentTrip) {
              setAllSelectedTrips((prev) => {
                if (prev.some((x) => String(x.id) === id)) return prev;

                return [...prev, currentTrip];
              });
            }

            if (!checked) {
              setAllSelectedTrips((prev) =>
                prev.filter((x) => String(x.id) !== id),
              );
            }
          };

          return (
            <Form>
              <Row className="g-4">
                {/* SESSION */}
                <Col md={3}>
                  <label className="form-label">
                    Session <span className="text-danger">*</span>
                  </label>

                  {(() => {
                    const selectedSessionObj = session?.find(
                      (s) => String(s.id) === String(values.sessionId),
                    );

                    const displayName =
                      selectedSessionObj?.name ||
                      billDetails?.sessionName ||
                      "";

                    return (
                      <>
                        <input
                          type="text"
                          className="form-control"
                          value={displayName}
                          disabled
                        />
                        <input
                          type="hidden"
                          name="sessionId"
                          value={values.sessionId || ""}
                        />
                      </>
                    );
                  })()}
                </Col>

                {/* CUSTOMER */}
                <Col md={3}>
                  <Label>
                    Customer <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={customersList.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label:
                              customersList.find(
                                (c) => c.id === values.customerId,
                              )?.name || "",
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("customerId", opt?.value || "");

                      dispatch(
                        fetchBillChargedTripsList({
                          customerId: opt?.value,
                          chargesBillInvoiceNo:
                            values.vehicleId === originalVehicleId
                              ? chargesInvoiceNo
                              : undefined,
                          vehicleId: values.vehicleId,
                          tripType: values.tripType,
                          isChargesBill: false,
                          isUpdateChargesBill: true,
                        }),
                      );

                      calculateTotals(values.tripIds);
                    }}
                  />
                </Col>

                {/* VEHICLE */}
                <Col md={3}>
                  <Label>
                    Vehicle <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={vehicles.map((v) => ({
                      value: v.id,
                      label: v.vehicleNo,
                    }))}
                    value={
                      values.vehicleId
                        ? {
                            value: values.vehicleId,
                            label:
                              vehicles.find((v) => v.id === values.vehicleId)
                                ?.vehicleNo || "",
                          }
                        : null
                    }
                    // onChange={(opt) => {
                    //   setFieldValue("vehicleId", opt?.value || "");
                    //   setTripInitialized(false);

                    //   dispatch(
                    //     fetchBillChargedTripsList({
                    //       customerId: values.customerId,
                    //       vehicleId: opt?.value,
                    //       sessionId: values.sessionId,
                    //       isChargesBill: false,
                    //       isUpdateBillOrCharge: false,
                    //     }),
                    //   );

                    //   setFieldValue("tripIds", []);
                    //   calculateTotals([]);
                    // }}

                    onChange={(opt) => {
                      const selectedVehicleId = opt?.value || "";

                      setFieldValue("vehicleId", selectedVehicleId);

                      const payload = {
                        customerId: values.customerId,
                        vehicleId: selectedVehicleId,
                        sessionId: values.sessionId,
                        fromDate: values.fromDate,
                        toDate: values.toDate,
                        tripType: values.tripType,
                        isChargesBill: false,
                        isUpdateChargesBill: true,
                      };

                      // Send invoice only when original vehicle is selected again
                      if (selectedVehicleId === originalVehicleId) {
                        payload.chargesBillInvoiceNo = chargesInvoiceNo;
                      }
                      dispatch(fetchBillChargedTripsList(payload));

                      calculateTotals(values.tripIds);
                    }}
                  />
                </Col>

                <Col md={3}>
                  <Label>Trip Type </Label>

                  <Select
                    options={tripOptions}
                    value={
                      tripOptions.find(
                        (item) => item.value === values.tripType,
                      ) || null
                    }
                    onChange={(opt) => {
                      setFieldValue("tripType", opt?.value);
                      dispatch(
                        fetchBillChargedTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId: values.supplierId,
                          sessionId: values.sessionId,
                          chargesBillInvoiceNo: chargesInvoiceNo,
                          tripType: opt.value,
                          isChargesBill: false,
                          isUpdateChargesBill: true,
                        }),
                      );

                      calculateTotals(values.tripIds);
                    }}
                    onBlur={() => setFieldTouched("tripType", true)}
                    placeholder="Select Trip Type"
                  />
                  <ErrorMessage
                    name="tripType"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                {/* SUPPLIER */}
                {/* <Col md={3}> */}
                {/* <Label>Supplier</Label>
                  <Select
                    options={suppliers.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const supplierId = opt ? opt.value : null;

                      setFieldValue("supplierId", supplierId);
                      dispatch(
                        fetchBillChargedTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId,
                          sessionId: values.sessionId,
                          chargesBillInvoiceNo: chargesInvoiceNo,
                          isChargesBill: false,
                          isUpdateChargesBill: true,
                        }),
                      );

                      calculateTotals(values.tripIds);
                    }}
                    isClearable
                  /> */}
                {/* </Col> */}

                {/* BILL DATE */}
                <Col md={3}>
                  <Label>
                    Bill Date <span className="text-danger">*</span>
                  </Label>
                  {/* <Flatpickr
                    className="form-control"
                    value={values.billDate}
                    onChange={(dates) => setFieldValue("billDate", dates[0])}
                    options={{ dateFormat: "d-m-Y", allowInput: true }}
                  /> */}

                  <Flatpickr
                    className="form-control"
                    value={values.billDate}
                    options={{ dateFormat: "d-m-Y", allowInput: true }}
                    onChange={(dates) => {
                      if (dates[0]) setFieldValue("billDate", dates[0]);
                    }}
                    onClose={(dates) => {
                      if (dates[0]) setFieldValue("billDate", dates[0]);
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Label>From Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select From Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.fromDate}
                      onChange={(date) => {
                        const fromDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0",
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;
                        setFieldValue("fromDate", fromDateValue);

                        if (fromDateValue && values.toDate) {
                          dispatch(
                            fetchBillChargedTripsList({
                              customerId: values.customerId,
                              sessionId: values.sessionId,
                              fromDate: fromDateValue,
                              toDate: values.toDate,
                              isChargesBill: false,
                              isUpdateChargesBill: true,
                              vehicleId: values.vehicleId,
                              chargesBillInvoiceNo: chargesInvoiceNo,
                            }),
                          );
                        }

                        calculateTotals(values.tripIds);
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>To Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select To Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.toDate}
                      onChange={(date) => {
                        const toDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0",
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;

                        setFieldValue("toDate", toDateValue);

                        if (values.fromDate && toDateValue) {
                          dispatch(
                            fetchBillChargedTripsList({
                              customerId: values.customerId,
                              sessionId: values.sessionId,
                              fromDate: values.fromDate,
                              toDate: toDateValue,
                              pageNo: 0,
                              isChargesBill: false,
                              isUpdateChargesBill: true,
                              vehicleId: values.vehicleId,
                              chargesBillInvoiceNo: chargesInvoiceNo,
                            }),
                          );
                        }

                        calculateTotals(values.tripIds);
                      }}
                    />

                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div style={{ width: "285px" }}>
                    <Label className="">Note:</Label>
                    <Input
                      type="text"
                      value={values.note}
                      onChange={(e) => setFieldValue("note", e.target.value)}
                      placeholder="Enter note"
                    />
                  </div>
                </Col>

                {/* TRIP TABLE */}
                <Col md={12}>
                  {/* <Label>Select Trip(s) *</Label>
                   */}

                  <div className="d-flex justify-content-between align-items-center">
                    <Label>Selected Trips</Label>

                    {/* <div style={{ width: "285px" }}>
                      <Label className="mb-0">Note:</Label>
                      <Input
                        type="text"
                        value={values.note}
                        onChange={(e) => setFieldValue("note", e.target.value)}
                        placeholder="Enter note"
                      />
                    </div> */}
                  </div>

                  <Table bordered size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Remove</th>
                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                        <th>Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {allSelectedTrips.map((trip) => (
                        <tr key={trip.id}>
                          <td>
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => handleTripToggle(trip.id, false)}
                            >
                              Remove
                            </Button>
                          </td>
                          <td>{trip.customerName}</td>
                          <td>{trip.suppliersName}</td>
                          <td>{trip.tripDate}</td>
                          <td>{trip.vehicleNo}</td>
                          <td>{trip.rate}</td>
                          <td>{trip.kmOrTrip}</td>
                          <td>{trip.tollCharges}</td>
                          <td>{trip.handlingCharges}</td>
                          <td>{trip.otherCharges}</td>
                          <td>{trip.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="d-flex justify-content-between align-items-center">
                    <Label>
                      Select Trips <span className="text-danger">*</span>
                    </Label>
                  </div>

                  <Table bordered className="mt-2">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                        <th>Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="8" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}

                      {!loading && availableTrips.length === 0 && (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            No Record Found
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        availableTrips.map((trip) => {
                          const checked = (values.tripIds || [])
                            .map(String)
                            .includes(String(trip.id));

                          return (
                            <tr key={trip.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    handleTripToggle(trip.id, e.target.checked)
                                  }
                                />
                              </td>
                              <td>{trip.customerName}</td>
                              <td>{trip.suppliersName}</td>
                              <td>{trip.tripDate}</td>
                              <td>{trip.vehicleNo}</td>
                              <td>{trip.rate}</td>
                              <td>{trip.kmOrTrip}</td>
                              <td>{trip.tollCharges}</td>
                              <td>{trip.handlingCharges}</td>
                              <td>{trip.otherCharges}</td>
                              <td>{trip.amount}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </Col>

                {/* TOTAL SUMMARY */}
                <Col md={12}>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                        <th>Grand Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>₹ {totals.tollCharges.toFixed(2)}</td>
                        <td>₹ {totals.handlingCharges.toFixed(2)}</td>
                        <td>₹ {totals.otherCharges.toFixed(2)}</td>
                        <td>
                          <b>₹ {totals.grandTotal.toFixed(2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* SUBMIT */}
                <Col md={12} className="text-end">
                  <Button
                    color="success"
                    className="btn-sm"
                    type="submit"
                    disabled={hasMixedSelection}
                  >
                    Update Bill
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EditExtraChargeBill;
